/* eslint-disable max-len */
import React, { useState, useEffect, useLayoutEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import { variance } from 'mathjs';
import { Button, Container, Col, Div, Icon, Row, Text } from '@pinpoint/design-kit';

import Radar from 'src/charts/radar';
import Bar from 'src/charts/bar';
import Tornado from 'src/charts/tornado';
import DeepOcean from 'src/charts/deep_ocean';
import PxHistogram from 'src/charts/px_histogram';

import { pxScoresPropType, stylePropType, pxHistogramsPropType } from 'src/PropTypes';
import { Style1 as Style } from 'src/styles';
import { EventEmitter } from 'src/components';

const eventEmitter = new EventEmitter();

const SquishlessRadar = Radar;
const SquishlessBar = Bar;
const SquishlessTornado = Tornado;
const SquishlessDeepOcean = DeepOcean;

/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable react/prop-types */

function PxHistogramContent({
  refresh,
  pxHistograms,
  theme,
  onThemeChange,
  trait: initialTrait,
  defaultTheme,
  customName = null,
}) {
  const [selectedTrait, setSelectedTrait] = useState(initialTrait);
  const [selectedViz, setSelectedViz] = useState();
  eventEmitter.subscribe('selectedTrait', (trait) => {
    if (trait) {
      setSelectedTrait(trait);
    }
  });
  eventEmitter.subscribe('viz', (viz) => {
    setSelectedViz(viz);
  });
  if (!refresh || !refresh.pxHistograms || !refresh.pxHistograms.length || !pxHistograms || !pxHistograms.length) {
    return null;
  }
  const pxHistogram = pxHistograms.find(({ trait }) => trait.code === selectedTrait.code);
  const populationHistogram = refresh.pxHistograms.find(({ trait }) => trait.code === selectedTrait.code);
  return (
    <Div w="100%" h="100%">
      <PxHistogram
        theme={theme}
        defaultTheme={defaultTheme}
        series={[
          {
            name: customName || selectedTrait.trait,
            values: pxHistogram.frequencies,
            theme,
          },
          {
            name: 'US Population',
            values: populationHistogram.frequencies,
          },
        ]}
        options={{
          legend: {
            top: 'bottom',
            padding: [0, 0, 24, 0],
          },
          grid: {
            top: '23%',
            bottom: '20%',
          },
          series: [
            {
              itemStyle: {
                color: selectedViz === 'DEEP_OCEAN' && selectedTrait.oceanColor ? selectedTrait.oceanColor : null,
              },
            },
            {
              itemStyle: {
                color: '#A0A0A0',
              },
            },
          ],
        }}
        onThemeChange={(nextTheme) => {
          onThemeChange(nextTheme);
        }}
      />
    </Div>
  );
}

function TextContent({ trait: initialTrait, values: initialValues }) {
  const [selectedTrait, setSelectedTrait] = useState(initialTrait);
  const [values, setValues] = useState(initialValues);

  eventEmitter.subscribe('selectedTrait', (trait) => {
    if (trait) {
      setSelectedTrait(trait);
    }
  });
  eventEmitter.subscribe('traitValues', (values_) => {
    if (values_) {
      setValues(values_);
    }
  });

  useEffect(() => {
    setSelectedTrait(initialTrait);
  }, [initialTrait]);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const { trait, highRangeName, lowRangeName, description } = selectedTrait;

  return (
    <Div d="flex" flexDir="column" justify="flex-start" align="flex-start" w="100%" h="100%" paddingRight="1rem">
      <Text textSize="title" textColor="tabSelected" textAlign="left" m={{ y: '0' }} p={{ x: 0, b: '0.75rem' }}>
        {values !== null ? (values[0] < 50 ? lowRangeName : highRangeName) : trait}
      </Text>
      <Text textColor="tabSelected" textSize="subheader" textAlign="left" style={{ lineHeight: '1.4' }}>
        {description}
      </Text>
    </Div>
  );
}

function DeepOceanContent({ labels, series, style, ...rest }) {
  return (
    <SquishlessDeepOcean
      title=""
      labels={labels}
      series={series}
      style={style}
      onMouseover={({ label, values }) => {
        eventEmitter.dispatch('selectedTrait', label);
        eventEmitter.dispatch('traitValues', values);
      }}
      {...rest}
    />
  );
}

function RadarContent({ labels, series, style, view, ...rest }) {
  return (
    <SquishlessRadar
      title=""
      labels={labels}
      series={series}
      style={style}
      onMouseover={({ label, values }) => {
        if (label) {
          eventEmitter.dispatch('selectedTrait', label);
          eventEmitter.dispatch('traitValues', values);
        }
      }}
      formatter={(params) => (labels.length < 30 ? params.value.toFixed(1) : params.value.toFixed(0))}
      options={{
        radar: {
          shape: view === 'ALL' ? 'circle' : 'radar',
          radius: view === 'ALL' ? '80%' : '78%',
          name: {
            padding: 2,
            fontSize: view === 'ALL' ? 12 : 14,
          },
        },
        label: {
          textSize: {
            fontSize: view === 'ALL' ? 12 : 14,
          },
        },
        legend: {
          padding: [0, 0, 24, 0],
        },
      }}
      {...rest}
    />
  );
}

function BarContent({ labels, series, style, view, ...rest }) {
  return (
    <SquishlessBar
      title=""
      labels={labels}
      series={series}
      style={style}
      showRange
      onMouseover={({ label, values }) => {
        if (label) {
          eventEmitter.dispatch('selectedTrait', label);
          eventEmitter.dispatch('traitValues', values);
        }
      }}
      options={{
        xAxis: {
          axisLabel: {
            fontSize: 16,
          },
        },
        label: {
          fontSize: labels.length < 30 ? 14 : 10,
        },
      }}
      height={view === 'ALL' ? '120%' : undefined}
      {...rest}
    />
  );
}

function TornadoContent({ labels, series, style, view, ...rest }) {
  return (
    <SquishlessTornado
      title=""
      labels={labels}
      series={series}
      style={style}
      onMouseover={({ label, values }) => {
        if (label) {
          eventEmitter.dispatch('selectedTrait', label);
          eventEmitter.dispatch('traitValues', values);
        }
      }}
      options={{
        label: {
          fontSize: labels.length < 30 ? 14 : 10,
        },
      }}
      height={view === 'ALL' ? '120%' : undefined}
      {...rest}
    />
  );
}

function Segment({
  object: _object,
  style,
  styleOverride,
  minW,
  minH,
  theme: themeProp,
  relative,
  onThemeChange,
  defaultTheme,
}) {
  const [viz, setViz] = useState('RADAR');
  const [prevViz, setPrevViz] = useState('RADAR');
  const [layout, setLayout] = useState('TWO_COLUMN');
  const [prevLayout, setPrevLayout] = useState('TWO_COLUMN');
  const [view, setView] = useState('TOP7');
  const [theme, setTheme] = useState(themeProp);
  const [isOceanStyle, setOceanStyle] = useState(false);

  useEffect(() => {
    eventEmitter.dispatch('viz', viz);
  }, [viz]);

  useEffect(() => {
    if (_object.theme) {
      setTheme(_object.theme);
    } else {
      setTheme(themeProp);
    }
  }, [themeProp, _object]);

  if (!_object) {
    return <Div>No Data</Div>;
  }
  const _objects = [_object];
  const objects = [];
  const segments = [];
  _objects.forEach(({ pxScores, ...rest }) => {
    const pxScoresCopy = pxScores.filter(({ trait: { subType } }) => subType !== 'factor' && subType !== 'redacted');
    pxScoresCopy.sort(({ trait: { code: codeA } }, { trait: { code: codeB } }) => (codeA > codeB ? 1 : -1));
    objects.push({
      ...rest,
      pxScores: pxScoresCopy,
    });
  });

  const traits = objects[0].pxScores.map(({ trait }) => trait);
  for (let i = 0; i < traits.length; i += 1) {
    if (traits[i].subType !== 'factor' && traits[i].subType !== 'redacted') {
      const scores = objects.map(({ pxScores }) => pxScores[i].score);
      segments.push({
        trait: traits[i],
        scores,
        order: scores.length > 1 ? variance(...scores) || 0.01 : Math.abs(50 - scores[0]),
      });
    }
  }

  segments.sort(({ order: orderA }, { order: orderB }) => orderB - orderA);
  // Isolate the labels
  const labels = segments
    .filter(
      ({ trait: { traitType, subType } }) =>
        (traitType === 'demographic' && subType !== 'redacted') || (traitType === 'psychometric' && subType === 'facet')
    )
    .map(({ trait }) => trait);
  const series = [];
  for (let i = 0; i < objects.length; i += 1) {
    series.push({
      name: objects[i].name,
      values: segments.map(({ scores }) => scores[i]),
      theme: objects[i].theme,
    });
  }

  const demographicLabels = segments
    .filter(({ trait: { traitType, subType } }) => traitType === 'demographic' && subType !== 'redacted')
    .map(({ trait }) => trait);
  const demographicSeries = [];
  for (let i = 0; i < objects.length; i += 1) {
    demographicSeries.push({
      name: objects[i].name,
      values: segments
        .filter(({ trait: { traitType, subType } }) => traitType === 'demographic' && subType !== 'redacted')
        .map(({ scores }) => scores[i]),
      theme: objects[i].theme,
    });
  }

  const oceanLabels = segments
    .filter(({ trait: { traitType, subType } }) => traitType === 'psychometric' && subType === 'facet')
    .map(({ trait }) => trait);
  const oceanSeries = [];
  for (let i = 0; i < objects.length; i += 1) {
    oceanSeries.push({
      name: objects[i].name,
      values: segments
        .filter(({ trait: { traitType, subType } }) => traitType === 'psychometric' && subType === 'facet')
        .map(({ scores }) => scores[i]),
      theme: objects[i].theme,
    });
  }

  const top7Labels = labels.slice(0, 7);
  const top7Series = series.map(({ name, theme: segmentTheme, values }) => ({
    name,
    theme: segmentTheme,
    values: values.slice(0, 7),
  }));

  const { refresh, pxHistograms } = objects[0];

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <Container
      h="100%"
      minH={minH}
      w="100%"
      minW={minW}
      bg="secondary"
      rounded="md"
      style={{
        paddingLeft: '0rem',
        paddingRight: '0rem',
        ...styleOverride,
      }}
    >
      <Row w="100%" m={{ y: '1.25rem' }}>
        <Col size={{ xs: '12', lg: '7' }} d="flex" flexDir="column">
          <Div d="flex" flexDir="row" justify="flex-start" p={{ l: '.75rem' }} data-test="trait-btns-div">
            <Button
              bg={view === 'TOP7' ? 'brand500' : 'tabRest'}
              bgHover={view === 'TOP7' ? 'brand500' : 'tabSelected'}
              textColor={view === 'TOP7' ? 'white' : 'white'}
              m={{ r: '1em' }}
              data-tip="View top 7 traits"
              onClick={() => {
                setView('TOP7');
                if (viz === 'DEEP_OCEAN') {
                  setViz(prevViz);
                  setLayout(prevLayout);
                }
                eventEmitter.dispatch('selectedTrait', labels[0]);
                // NOTE: SelectedValues are not dispatched as events
                // here but it is not used in the viz, so no harm there.
              }}
            >
              TOP 7
            </Button>
            <Button
              bg={view === 'DEEP_OCEAN' ? 'brand500' : 'tabRest'}
              bgHover={view === 'DEEP_OCEAN' ? 'brand500' : 'tabSelected'}
              textColor={view === 'DEEP_OCEAN' ? 'white' : 'white'}
              m={{ r: '1em' }}
              data-tip="View Deep OCEAN traits only"
              onClick={() => {
                setView('DEEP_OCEAN');
                eventEmitter.dispatch('selectedTrait', oceanLabels[0]);
              }}
            >
              DEEP OCEAN
            </Button>
            <Button
              bg={view === 'DEMOGRAPHIC' ? 'brand500' : 'tabRest'}
              bgHover={view === 'DEMOGRAPHIC' ? 'brand500' : 'tabSelected'}
              textColor={view === 'DEMOGRAPHIC' ? 'white' : 'white'}
              m={{ r: '1em' }}
              data-tip="View demographics only"
              disabled={demographicLabels.length === 0}
              onClick={() => {
                setView('DEMOGRAPHIC');
                if (viz === 'DEEP_OCEAN') {
                  setViz(prevViz);
                  setLayout(prevLayout);
                }
                eventEmitter.dispatch('selectedTrait', demographicLabels[0]);
              }}
            >
              DEMOGRAPHICS
            </Button>
            <Button
              bg={view === 'ALL' ? 'brand500' : 'tabRest'}
              bgHover={view === 'ALL' ? 'brand500' : 'tabSelected'}
              textColor={view === 'ALL' ? 'white' : 'white'}
              disabled={viz === 'RADAR'}
              m={{ r: '1em' }}
              data-tip="View all traits"
              onClick={() => {
                setView('ALL');
                if (viz === 'DEEP_OCEAN') {
                  setViz(prevViz);
                  setLayout(prevLayout);
                }
                eventEmitter.dispatch('selectedTrait', labels[0]);
              }}
            >
              ALL
            </Button>
          </Div>
          <Div d="flex" flexDir="row" justify="center" align="flex-end" h="8rem" data-test="chart-choice-btns">
            {view === 'DEEP_OCEAN' ? (
              <Button
                color="white"
                bg={viz === 'DEEP_OCEAN' ? 'brand500' : 'tabRest'}
                bgHover={viz === 'DEEP_OCEAN' ? 'brand500' : 'tabSelected'}
                m={{ x: '0.375em' }}
                data-tip="Rose chart"
                onClick={() => {
                  setPrevViz(viz);
                  setPrevLayout(layout);
                  setViz('DEEP_OCEAN');
                  setLayout('TWO_COLUMN');
                  eventEmitter.dispatch('selectedTrait', oceanLabels[0]);
                }}
              >
                <Icon name="DeepOcean" color={viz === 'DEEP_OCEAN' ? 'white' : 'white'} />
              </Button>
            ) : null}
            <Button
              bg={viz === 'RADAR' ? 'brand500' : 'tabRest'}
              bgHover={viz === 'RADAR' ? 'brand500' : 'tabSelected'}
              m={{ x: '.375em' }}
              data-tip="Radar chart"
              color="white"
              disabled={view === 'ALL'}
              onClick={() => {
                setViz('RADAR');
                setLayout('TWO_COLUMN');
                if (view === 'DEMOGRAPHIC') {
                  eventEmitter.dispatch('selectedTrait', demographicLabels[0]);
                } else if (view === 'DEEP_OCEAN') {
                  eventEmitter.dispatch('selectedTrait', demographicLabels[0]);
                } else {
                  eventEmitter.dispatch('selectedTrait', labels[0]);
                }
              }}
            >
              <Icon name="Radar" color={viz === 'RADAR' ? 'white' : 'white'} />
            </Button>
            <Button
              bg={viz === 'BAR' ? 'brand500' : 'tabRest'}
              bgHover={viz === 'BAR' ? 'brand500' : 'tabSelected'}
              color="white"
              m={{ x: '0.375em' }}
              data-tip="Bar chart"
              onClick={() => {
                setViz('BAR');
                setLayout('ONE_COLUMN');
                if (view === 'DEMOGRAPHIC') {
                  eventEmitter.dispatch('selectedTrait', demographicLabels[0]);
                } else if (view === 'DEEP_OCEAN') {
                  eventEmitter.dispatch('selectedTrait', demographicLabels[0]);
                } else {
                  eventEmitter.dispatch('selectedTrait', labels[0]);
                }
              }}
            >
              <Icon name="BarChart" color={viz === 'BAR' ? 'white' : 'white'} />
            </Button>
            <Button
              bg={viz === 'TORNADO' ? 'brand500' : 'tabRest'}
              bgHover={viz === 'TORNADO' ? 'brand500' : 'tabSelected'}
              m={{ x: '0.375em' }}
              data-tip="Tornado chart"
              onClick={() => {
                setViz('TORNADO');
                setLayout('ONE_COLUMN');
                if (view === 'DEMOGRAPHIC') {
                  eventEmitter.dispatch('selectedTrait', demographicLabels[0]);
                } else if (view === 'DEEP_OCEAN') {
                  eventEmitter.dispatch('selectedTrait', demographicLabels[0]);
                } else {
                  eventEmitter.dispatch('selectedTrait', labels[0]);
                }
              }}
            >
              <Icon name="Butterfly" color={viz === 'TORNADO' ? 'white' : 'white'} />
            </Button>
          </Div>
        </Col>
        <Col size={{ xs: '12', lg: '5' }} h="10.5rem">
          <Div
            p={{ l: '1.25rem', t: '1rem', b: '1rem' }}
            rounded="md"
            h="100%"
            bg="secondary"
            m={{ r: '1rem' }}
            data-test="desc-div"
          >
            <Div h="100%" style={{ 'overflow-y': 'scroll', 'overflow-x': 'hidden' }} p={{ r: '2rem' }}>
              <TextContent trait={labels[0]} values={[series[0].values[0]]} />
            </Div>
          </Div>
        </Col>
      </Row>
      <Row h="calc(100% - 14.25rem)" w="100%" m="0">
        <Col size={{ xs: '12', lg: layout === 'TWO_COLUMN' ? '7' : '12' }} h={{ xs: '100%', lg: '100%' }} minH="35vh">
          <Div
            bg="secondary"
            rounded="md"
            h="calc(100%)"
            w={layout === 'TWO_COLUMN' ? 'calc(100% - 0.75rem)' : 'calc(100% - 1.5rem)'}
            p={{ y: '1.25rem', x: '1.5rem' }}
            d="flex"
            justify="flex-start"
            align="center"
            flexDir="column"
            m={{ l: '.75rem', r: layout === 'TWO_COLUMN' ? '.75rem' : '1.5rem' }}
          >
            {viz === 'DEEP_OCEAN' ? (
              <DeepOceanContent labels={oceanLabels} series={oceanSeries} style={style} sortByFacet />
            ) : null}
            {viz === 'RADAR' ? (
              <RadarContent
                labels={
                  view === 'TOP7'
                    ? top7Labels
                    : view === 'DEEP_OCEAN'
                      ? oceanLabels
                      : view === 'DEMOGRAPHIC'
                        ? demographicLabels
                        : labels
                }
                series={
                  view === 'TOP7'
                    ? top7Series
                    : view === 'DEEP_OCEAN'
                      ? oceanSeries
                      : view === 'DEMOGRAPHIC'
                        ? demographicSeries
                        : series
                }
                defaultTheme={defaultTheme}
                style={style}
                relative={relative}
                theme={theme}
                showRangeControl
                onThemeChange={(nextTheme) => {
                  setTheme(nextTheme);
                  onThemeChange(objects[0].id, nextTheme);
                }}
                view={view}
              />
            ) : null}
            {viz === 'BAR' ? (
              <BarContent
                labels={
                  view === 'TOP7'
                    ? top7Labels
                    : view === 'DEEP_OCEAN'
                      ? oceanLabels
                      : view === 'DEMOGRAPHIC'
                        ? demographicLabels
                        : labels
                }
                series={
                  view === 'TOP7'
                    ? top7Series
                    : view === 'DEEP_OCEAN'
                      ? oceanSeries
                      : view === 'DEMOGRAPHIC'
                        ? demographicSeries
                        : series
                }
                style={style}
                relative={relative}
                theme={theme}
                defaultTheme={defaultTheme}
                oceanStyle={isOceanStyle}
                onThemeChange={(nextTheme) => {
                  setTheme(nextTheme);
                  onThemeChange(objects[0].id, nextTheme);
                }}
                onOceanStyleChange={(nextOceanStyle) => {
                  setOceanStyle(nextOceanStyle);
                }}
                view={view}
              />
            ) : null}
            {viz === 'TORNADO' ? (
              <TornadoContent
                labels={
                  view === 'TOP7'
                    ? top7Labels
                    : view === 'DEEP_OCEAN'
                      ? oceanLabels
                      : view === 'DEMOGRAPHIC'
                        ? demographicLabels
                        : labels
                }
                series={
                  view === 'TOP7'
                    ? top7Series
                    : view === 'DEEP_OCEAN'
                      ? oceanSeries
                      : view === 'DEMOGRAPHIC'
                        ? demographicSeries
                        : series
                }
                style={style}
                relative={relative}
                theme={theme}
                defaultTheme={defaultTheme}
                oceanStyle={isOceanStyle}
                onThemeChange={(nextTheme) => {
                  setTheme(nextTheme);
                  onThemeChange(objects[0].id, nextTheme);
                }}
                onOceanStyleChange={(nextOceanStyle) => {
                  setOceanStyle(nextOceanStyle);
                }}
                view={view}
              />
            ) : null}
          </Div>
        </Col>
        <Col size={{ xs: '12', lg: '5' }}>
          {layout === 'TWO_COLUMN' ? (
            <Div
              h="100%"
              rounded="md"
              border="1px solid silver"
              m={{ l: '1px', r: '.9375rem' }}
              data-test="histogram-div"
            >
              <PxHistogramContent
                customName={objects[0].name}
                refresh={refresh}
                pxHistograms={pxHistograms}
                trait={labels[0]}
                theme={theme}
                defaultTheme={defaultTheme}
                onThemeChange={(nextTheme) => {
                  setTheme(nextTheme);
                  onThemeChange(objects[0].id, nextTheme);
                }}
              />
            </Div>
          ) : null}
        </Col>
      </Row>
    </Container>
  );
}

Segment.propTypes = {
  object: PropTypes.shape({
    name: PropTypes.string,
    theme: PropTypes.string,
    pxScores: pxScoresPropType,
    pxHistograms: pxHistogramsPropType,
  }).isRequired,
  style: stylePropType,
  styleOverride: PropTypes.shape({}),
  minH: PropTypes.string,
  minW: PropTypes.string,
  theme: PropTypes.string,
  relative: PropTypes.bool,
  onThemeChange: PropTypes.func,
};

Segment.defaultProps = {
  style: Style,
  styleOverride: {
    background: Style.global.backgroundColor,
  },
  minH: '100vh',
  minW: '100vw',
  theme: 'pinpoint',
  relative: true,
  onThemeChange: () => null,
};

export default Segment;
